import React from 'react';
import { CheckCheck, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * ChecklistIndicator - A component that displays checklist progress with a hover tooltip
 * 
 * @param {Object} props - Component props
 * @param {Array} props.checklist - Array of checklist items with {label, checked} properties
 * @param {string} props.className - Additional CSS classes
 * @param {'sm'|'md'|'lg'} props.size - Size of the indicator icon
 * @param {boolean} props.showProgressBar - Whether to show a small progress bar
 * 
 * @example
 * <ChecklistIndicator 
 *   checklist={task.checklist} 
 *   size="sm" 
 *   showProgressBar={true}
 * />
 */
export function ChecklistIndicator({ 
  checklist = [], 
  className = '',
  size = 'md',
  showProgressBar = false
}) {
  if (!checklist || checklist.length === 0) {
    return null;
  }

  const completedCount = checklist.filter(item => item.checked).length;
  const totalCount = checklist.length;
  const progressPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 100;

  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  const getProgressColor = (percentage) => {
    if (percentage === 100) return 'text-green-600 dark:text-green-400';
    if (percentage >= 75) return 'text-blue-600 dark:text-blue-400';
    if (percentage >= 50) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-orange-600 dark:text-orange-400';
  };

  const getProgressIcon = (percentage) => {
    if (percentage === 100) return CheckCheck;
    return CheckSquare;
  };

  const getProgressText = (percentage) => {
    if (percentage === 100) return 'Complete';
    if (percentage >= 75) return 'Almost Done';
    if (percentage >= 50) return 'In Progress';
    if (percentage >= 25) return 'Getting Started';
    return 'Not Started';
  };

  const IconComponent = getProgressIcon(progressPercentage);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn(
            'inline-flex items-center gap-1 cursor-help hover:opacity-80 transition-opacity',
            className
          )}>
            <IconComponent 
              className={cn(
                sizeClasses[size],
                getProgressColor(progressPercentage)
              )} 
            />
            <span className="text-xs text-muted-foreground font-medium">
              {completedCount}/{totalCount}
            </span>
            {showProgressBar && (
              <div className="w-8 h-1 bg-muted rounded-full overflow-hidden">
                <div 
                  className={cn(
                    'h-full transition-all duration-300',
                    progressPercentage === 100 ? 'bg-green-500' :
                    progressPercentage >= 75 ? 'bg-blue-500' :
                    progressPercentage >= 50 ? 'bg-yellow-500' :
                    'bg-orange-500'
                  )}
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-sm">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-medium">Checklist Progress</div>
              <div className={cn(
                'text-xs px-2 py-1 rounded-full ml-2',
                progressPercentage === 100 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                progressPercentage >= 75 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                progressPercentage >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
              )}>
                {getProgressText(progressPercentage)}
              </div>
            </div>
            <div className="text-sm text-muted-foreground">
              {completedCount} of {totalCount} items completed ({progressPercentage}%)
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div 
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  progressPercentage === 100 ? 'bg-green-500' :
                  progressPercentage >= 75 ? 'bg-blue-500' :
                  progressPercentage >= 50 ? 'bg-yellow-500' :
                  'bg-orange-500'
                )}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {checklist.map((item, index) => (
                <div 
                  key={index} 
                  className={cn(
                    'text-xs flex items-center gap-2 p-1 rounded',
                    item.checked 
                      ? 'text-muted-foreground bg-muted/30' 
                      : 'text-foreground'
                  )}
                >
                  <div className={cn(
                    'w-2 h-2 rounded-full flex-shrink-0',
                    item.checked 
                      ? 'bg-green-500' 
                      : 'bg-muted-foreground/30'
                  )} />
                  <span className={cn(
                    'truncate',
                    item.checked && 'line-through'
                  )}>
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
} 